import React, { useCallback, useState } from "react";
import type { FiatPrice } from "@b2bportal/air-booking-api";
import {
  VipSupportSelectors,
  useCheckoutStateSelector as useSelector,
  VipSupportEventTypes,
  useCheckoutSend,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { SupportStatus } from "@hopper-b2b/types";
import { ButtonWrap } from "@hopper-b2b/ui";
import clsx from "clsx";

import VipSupportIcon from "../../../../../../assets/client/green-bolt.svg";
import BasicSupportIcon from "../../../../../../assets/client/headset.svg";
import "./styles.scss";
import type { TEvent } from "../../../../events";

interface VipSupportPanelProps {
  vipSupportPrice: FiatPrice;
}

export const VipSupportPanel = ({ vipSupportPrice }: VipSupportPanelProps) => {
  const { t, formatFiatCurrency } = useI18nContext();
  const vipSupportStatus = useSelector(VipSupportSelectors.getVipSupportStatus);
  const send = useCheckoutSend<TEvent>();

  const [selectedSupportOption, setSelectedSupportOption] =
    useState<SupportStatus>(vipSupportStatus);

  const handleOnClick = useCallback(
    (option: SupportStatus) => () => {
      setSelectedSupportOption(option);
      send({
        type: VipSupportEventTypes.TOGGLE_SUPPORT_STATUS,
        supportStatus: option,
      });
    },
    [setSelectedSupportOption, send]
  );

  return (
    <div className={clsx("mobile-vip-support-cards-section")}>
      <h3 className="vip-support-section-header">{t("vipSupport.header")}</h3>

      {/* VIP Support option */}
      <ButtonWrap
        className={clsx("fintech-purchase-card", {
          selected: selectedSupportOption === SupportStatus.vip,
        })}
        onClick={handleOnClick(SupportStatus.vip)}
      >
        <div className="vip-support-card-container">
          <img
            className={"support-icon"}
            src={VipSupportIcon}
            alt={t("vipSupportIconAlt")}
          />
          <div className="support-button-text-container">
            <div className="vip-support-card-header">
              <p className="vip-support-card-title">
                {t("vipSupport.vipTitle")}
              </p>
              <p className="vip-support-card-price">
                {`+${formatFiatCurrency(vipSupportPrice, {
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0,
                })}`}
              </p>
            </div>
            <div className="vip-support-card-description">
              <p className="vip-support-card-list-item-label">
                <span className="vip-support-subtitle-prefix">{"24/7 • "}</span>
                {t("vipSupport.vipSubtitle")}
              </p>
            </div>
          </div>
        </div>
      </ButtonWrap>

      {/* Basic Support option */}
      <ButtonWrap
        className={clsx("fintech-purchase-card basic-support-card", {
          selected: selectedSupportOption === SupportStatus.basic,
        })}
        onClick={handleOnClick(SupportStatus.basic)}
      >
        <div className="vip-support-card-container">
          <img
            className={"support-icon"}
            src={BasicSupportIcon}
            alt={t("basicSupportIconAlt")}
          />
          <div className="support-button-text-container">
            <div className="vip-support-card-header">
              <p className="vip-support-card-title">
                {t("vipSupport.basicTitle")}
              </p>
            </div>
            <div className="vip-support-card-description">
              <p className="vip-support-card-list-item-label">
                {t("vipSupport.basicSubtitle")}
              </p>
            </div>
          </div>
        </div>
      </ButtonWrap>

      <div className="mobile-review-flight-vip-support-divider" />
    </div>
  );
};
